import { useState, useEffect, useRef } from 'react';
import { enrollmentsApi, modulesApi, progressApi, facilitadoresApi, deliverablesApi, type ProgramEnrollment, type ProgramModule, type ClientModuleProgress, type ClientDeliverable } from '../services/programService';

const MODULO_ENTREGAVEIS: Record<string, { entregavel: string; ferramenta: string; descricao: string }> = {
  mapa_gargalos: {
    entregavel: 'Mapa Estratégico do Negócio',
    ferramenta: 'Mapa de Gargalos',
    descricao: 'Diagnóstico completo com identificação de gargalos operacionais e alavancas de crescimento.',
  },
  gestao_operacional: {
    entregavel: 'Organograma + Processos Documentados',
    ferramenta: 'Dashboard de Gestão',
    descricao: 'Estrutura organizacional definida com processos documentados e funções claras.',
  },
  financeiro: {
    entregavel: 'Planejamento Financeiro + Dashboard',
    ferramenta: 'Dashboard Financeiro + Assistentes IA',
    descricao: 'Controle financeiro completo com dashboard em tempo real e planejamentos.',
  },
  comercial: {
    entregavel: 'Plano Comercial + Métricas',
    ferramenta: 'Pipeline de Vendas',
    descricao: 'Estrutura comercial com processos de venda, métricas e funil definido.',
  },
  inovacao: {
    entregavel: 'Processos Otimizados + Automações',
    ferramenta: 'Automazioni e IA',
    descricao: 'Mapeamento e implementação de inovações nos processos existentes.',
  },
  ia_aplicada: {
    entregavel: 'Automações com IA',
    ferramenta: 'Agentes de IA',
    descricao: 'Aplicação de inteligência artificial em áreas-chave do negócio.',
  },
  juridico: {
    entregavel: 'Contratos e Documentos Jurídicos',
    ferramenta: 'Assistentes Jurídicos',
    descricao: 'Modelos de contratos, documentos legais e compliance estruturado.',
  },
};

const STATUS_OPTS = [
  { value: 'nao_iniciado', label: 'Não Iniciado', color: 'bg-gray-100 text-gray-500' },
  { value: 'em_consultoria', label: 'Em Consultoria', color: 'bg-amber-100 text-amber-700' },
  { value: 'entregue', label: 'Entregue', color: 'bg-blue-100 text-blue-700' },
  { value: 'aprovado', label: 'Aprovado', color: 'bg-green-100 text-green-700' },
];

export default function ModulosPage() {
  const [enrollments, setEnrollments] = useState<ProgramEnrollment[]>([]);
  const [modules, setModules] = useState<ProgramModule[]>([]);
  const [progress, setProgress] = useState<ClientModuleProgress[]>([]);
  const [facilitadores, setFacilitadores] = useState<any[]>([]);
  const [enrollmentId, setEnrollmentId] = useState('');
  const [selectedModule, setSelectedModule] = useState<string>('');
  const [deliverables, setDeliverables] = useState<ClientDeliverable[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingD, setSavingD] = useState(false);
  const [attachUrl, setAttachUrl] = useState('');
  const [showUrlInput, setShowUrlInput] = useState<string | null>(null);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const [ens, mods, facs] = await Promise.all([
        enrollmentsApi.list(),
        modulesApi.list(),
        facilitadoresApi.list(),
      ]);
      setEnrollments(ens);
      setModules(mods);
      setFacilitadores(facs);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (enrollmentId) {
      progressApi.list(enrollmentId).then(setProgress);
    } else {
      setProgress([]);
      setDeliverables([]);
      setSelectedModule('');
    }
  }, [enrollmentId]);

  const currentProgress = progress.find(p => (p as any).program_modules?.modulo_key === selectedModule);
  const currentModule = modules.find(m => m.modulo_key === selectedModule);
  const moduleInfo = selectedModule ? MODULO_ENTREGAVEIS[selectedModule] : null;

  useEffect(() => {
    if (currentProgress?.id) {
      deliverablesApi.list(currentProgress.id).then(setDeliverables);
    } else {
      setDeliverables([]);
    }
  }, [currentProgress?.id]);

  const updateProgress = async (id: string, status: string, facilitadorId: string) => {
    const mod = modules.find(m => (progress.find(p => p.id === id) as any)?.program_modules?.modulo_key === m.modulo_key);
    await progressApi.upsert({ id, enrollment_id: enrollmentId, module_id: mod?.id || '', status, facilitador_id: facilitadorId } as any);
    setProgress(await progressApi.list(enrollmentId));
  };

  const handleAddDeliverable = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentProgress?.id) return;
    setSavingD(true);
    const form = e.target as HTMLFormElement;
    const data = new FormData(form);
    await deliverablesApi.upsert({
      client_module_id: currentProgress.id,
      titulo: data.get('titulo') as string,
      descricao: data.get('descricao') as string,
      status: 'pendente',
    });
    setDeliverables(await deliverablesApi.list(currentProgress.id));
    (form.querySelector('[name="titulo"]') as HTMLInputElement).value = '';
    (form.querySelector('[name="descricao"]') as HTMLInputElement).value = '';
    setSavingD(false);
  };

  const handleDeliverableStatus = async (id: string, status: string) => {
    const d = deliverables.find(x => x.id === id);
    if (!d) return;
    await deliverablesApi.upsert({ ...d, status, data_entrega: status === 'entregue' || status === 'aprovado' ? new Date().toISOString().slice(0, 10) : d.data_entrega });
    if (currentProgress?.id) setDeliverables(await deliverablesApi.list(currentProgress.id));
  };

  const handleAttachUrl = async (d: ClientDeliverable) => {
    if (!attachUrl.trim() || !d.id) return;
    setSavingD(true);
    const urlToSave = attachUrl.trim().startsWith('http') ? attachUrl.trim() : `https://${attachUrl.trim()}`;
    await deliverablesApi.upsert({ ...d, arquivo_url: urlToSave, arquivo_nome: d.titulo });
    setAttachUrl('');
    setShowUrlInput(null);
    if (currentProgress?.id) setDeliverables(await deliverablesApi.list(currentProgress.id));
    setSavingD(false);
  };

  const handleAttachFile = async (d: ClientDeliverable) => {
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = '.pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx';
    fileInput.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file || !d.id) return;
      setSavingD(true);
      const reader = new FileReader();
      reader.onload = async () => {
        const b64 = (reader.result as string).split(',')[1];
        await deliverablesApi.upsert({ ...d, arquivo_url: `data:${file.type};base64,${b64}`, arquivo_nome: file.name });
        if (currentProgress?.id) setDeliverables(await deliverablesApi.list(currentProgress.id));
        setSavingD(false);
      };
      reader.readAsDataURL(file);
    };
    fileInput.click();
  };

  const getStatusCfg = (s: string) => STATUS_OPTS.find(x => x.value === s) || STATUS_OPTS[0];

  return (
    <div className="p-6">
      <h1 className="text-2xl font-black text-gray-900 mb-6">Progresso dos Módulos</h1>

      <div className="mb-6">
        <label className="text-xs text-gray-500 mb-1 block">Selecione o Cliente / Inscrição</label>
        <select value={enrollmentId} onChange={e => { setEnrollmentId(e.target.value); setSelectedModule(''); }} className="w-full max-w-md border border-gray-200 rounded-xl px-4 py-2.5 text-sm bg-white">
          <option value="">— Selecione —</option>
          {enrollments.map(en => <option key={en.id} value={en.id}>{(en as any).leads?.nome || en.lead_id}</option>)}
        </select>
      </div>

      {enrollmentId && (
        <>
          {/* Module selector */}
          {modules.length > 0 && (
            <div className="mb-6">
              <label className="text-xs text-gray-500 mb-2 block">Selecione o Módulo</label>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {modules.map(mod => {
                  const prog = progress.find(p => (p as any).program_modules?.modulo_key === mod.modulo_key);
                  const statusCfg = prog ? getStatusCfg(prog.status) : STATUS_OPTS[0];
                  const info = MODULO_ENTREGAVEIS[mod.modulo_key];
                  return (
                    <div
                      key={mod.id}
                      onClick={() => setSelectedModule(mod.modulo_key)}
                      className={`cursor-pointer rounded-2xl border p-4 transition-all hover:shadow-md ${selectedModule === mod.modulo_key ? 'border-amber-400 bg-amber-50' : 'border-gray-200 bg-white'}`}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center">
                          <i className={`${mod.icon || 'ri-folder-line'} text-amber-600 text-sm`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-bold text-gray-900 text-sm truncate">{info?.entregavel || mod.titulo}</h3>
                        </div>
                      </div>
                      <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${statusCfg.color}`}>{statusCfg.label}</span>
                      {prog && (
                        <div className="mt-2">
                          <div className="flex justify-between text-xs text-gray-500 mb-1">
                            <span>Progresso</span><span>{prog.progresso}%</span>
                          </div>
                          <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                            <div className="h-full bg-amber-500 rounded-full" style={{ width: `${prog.progresso}%` }} />
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Module detail */}
          {selectedModule && currentModule && moduleInfo && (
            <div className="bg-white rounded-2xl border border-gray-200 p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
                  <i className={`${currentModule.icon || 'ri-folder-line'} text-amber-600 text-xl`} />
                </div>
                <div>
                  <h2 className="font-black text-gray-900 text-lg">{moduleInfo.entregavel}</h2>
                  <p className="text-sm text-gray-500">{moduleInfo.descricao}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="bg-gray-50 rounded-xl p-4">
                  <p className="text-xs text-gray-500 mb-1">Ferramenta</p>
                  <p className="font-bold text-gray-900 text-sm">{moduleInfo.ferramenta}</p>
                </div>
                <div className="bg-gray-50 rounded-xl p-4">
                  <p className="text-xs text-gray-500 mb-1">Status Atual</p>
                  <select
                    value={currentProgress?.status || 'nao_iniciado'}
                    onChange={e => currentProgress?.id && updateProgress(currentProgress.id, e.target.value, currentProgress.facilitador_id || '')}
                    className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm bg-white mt-1"
                  >
                    {STATUS_OPTS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                </div>
              </div>

              {/* Facilitador */}
              <div className="mb-6">
                <p className="text-xs text-gray-500 mb-2">Facilitador responsável</p>
                <select
                  value={currentProgress?.facilitador_id || ''}
                  onChange={e => currentProgress?.id && updateProgress(currentProgress.id, currentProgress.status, e.target.value)}
                  className="w-full max-w-sm border border-gray-200 rounded-xl px-4 py-2.5 text-sm bg-white"
                >
                  <option value="">Selecione o facilitador</option>
                  {facilitadores.map(f => <option key={f.id} value={f.id}>{f.nome}</option>)}
                </select>
              </div>

              {/* Progress slider */}
              <div className="mb-6">
                <div className="flex justify-between text-xs text-gray-500 mb-2">
                  <span>Progresso</span>
                  <span>{currentProgress?.progresso || 0}%</span>
                </div>
                <input
                  type="range" min={0} max={100} step={25}
                  value={currentProgress?.progresso || 0}
                  onChange={async e => {
                    if (!currentProgress?.id || !currentModule.id) return;
                    await progressApi.upsert({ id: currentProgress.id, enrollment_id: enrollmentId, module_id: currentModule.id, progresso: +e.target.value, status: currentProgress.status, facilitador_id: currentProgress.facilitador_id } as any);
                    setProgress(await progressApi.list(enrollmentId));
                  }}
                  className="w-full accent-amber-500"
                />
                <div className="flex justify-between text-xs text-gray-400 mt-1">
                  <span>0%</span><span>25%</span><span>50%</span><span>75%</span><span>100%</span>
                </div>
              </div>

              {/* Deliverables list */}
              <div className="border-t border-gray-100 pt-5">
                <h3 className="font-bold text-gray-900 mb-3">Entregáveis</h3>

                {deliverables.length === 0 ? (
                  <p className="text-sm text-gray-400 text-center py-4">Nenhum entregável cadastrado.</p>
                ) : (
                  <div className="space-y-3 mb-4">
                    {deliverables.map(d => (
                      <div key={d.id} className="bg-gray-50 rounded-xl px-4 py-3">
                        <div className="flex items-start gap-3">
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-gray-900 text-sm">{d.titulo}</p>
                            {d.descricao && <p className="text-xs text-gray-400 mt-0.5">{d.descricao}</p>}
                            {/* Link/Arquivo anexado */}
                            {d.arquivo_url && (
                              d.arquivo_url.startsWith('data:') ? (
                                <a href={d.arquivo_url} download={d.arquivo_nome || 'arquivo'} className="inline-flex items-center gap-1 text-xs text-red-500 hover:text-red-600 mt-1 font-medium cursor-pointer">
                                  <i className="ri-file-pdf-line text-lg" />{d.arquivo_nome || 'Baixar PDF'}
                                </a>
                              ) : (
                                <a href={d.arquivo_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-blue-500 hover:text-blue-600 mt-1 font-medium cursor-pointer">
                                  <i className="ri-link text-lg" />{d.arquivo_nome || 'Abrir link'}
                                </a>
                              )
                            )}
                          </div>
                          <select
                            value={d.status}
                            onChange={e => handleDeliverableStatus(d.id, e.target.value)}
                            className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 bg-white shrink-0"
                          >
                            <option value="pendente">Pendente</option>
                            <option value="em_revisao">Em Revisão</option>
                            <option value="entregue">Entregue</option>
                            <option value="aprovado">Aprovado</option>
                          </select>
                        </div>
                        {/* Ações de anexo */}
                        <div className="flex items-center gap-2 mt-2">
                          <button
                            onClick={() => handleAttachFile(d)}
                            title="Anexar PDF ou documento"
                            className="flex items-center gap-1 text-xs bg-white border border-gray-200 hover:border-gray-300 text-gray-600 px-3 py-1.5 rounded-lg transition-colors cursor-pointer"
                          >
                            <i className="ri-file-add-line" />PDF/Doc
                          </button>
                          <button
                            onClick={() => setShowUrlInput(showUrlInput === d.id ? null : d.id)}
                            className="flex items-center gap-1 text-xs bg-white border border-gray-200 hover:border-gray-300 text-gray-600 px-3 py-1.5 rounded-lg transition-colors cursor-pointer"
                          >
                            <i className="ri-link" />Link
                          </button>
                          {(d.arquivo_url) && (
                            <button
                              onClick={async () => { await deliverablesApi.upsert({ ...d, arquivo_url: '', arquivo_nome: '' }); if (currentProgress?.id) setDeliverables(await deliverablesApi.list(currentProgress.id)); }}
                              className="flex items-center gap-1 text-xs bg-red-50 hover:bg-red-100 text-red-600 px-3 py-1.5 rounded-lg transition-colors cursor-pointer"
                            >
                              <i className="ri-delete-bin-line" />Remover
                            </button>
                          )}
                        </div>
                        {/* Input de URL */}
                        {showUrlInput === d.id && (
                          <div className="flex gap-2 mt-2">
                            <input
                              type="url"
                              placeholder="Cole o link (Google Drive, URL, etc.)"
                              value={attachUrl}
                              onChange={e => setAttachUrl(e.target.value)}
                              className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-xs"
                            />
                            <button
                              onClick={() => handleAttachUrl(d)}
                              disabled={savingD || !attachUrl.trim()}
                              className="bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-gray-950 font-bold text-xs px-3 py-2 rounded-lg transition-colors cursor-pointer"
                            >
                              Salvar
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Add deliverable form */}
                <form onSubmit={handleAddDeliverable} className="flex gap-2">
                  <input name="titulo" required placeholder="Nome do entregável" className="flex-1 border border-gray-200 rounded-xl px-3 py-2 text-sm" />
                  <input name="descricao" placeholder="Descrição (opcional)" className="flex-1 border border-gray-200 rounded-xl px-3 py-2 text-sm" />
                  <button type="submit" disabled={savingD} className="bg-amber-500 hover:bg-amber-400 disabled:opacity-60 text-gray-950 font-bold text-sm px-4 py-2 rounded-xl transition-colors cursor-pointer">
                    + Adicionar
                  </button>
                </form>
              </div>
            </div>
          )}
        </>
      )}

      {!enrollmentId && (
        <div className="text-center py-12 text-gray-400">Selecione um cliente para gerenciar seus módulos.</div>
      )}
    </div>
  );
}
